import { Avatar, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { RequestParticipant, SignRequest } from "models/interfaces";
import { convertESTtoUserLocalDateAndTime } from "helpers/methods";
import { getAvatarColors } from "utils/avatarColors";
import { focusRing } from "theme/colors";
import signRequestsStyles, { TAG_ACTION_CLASS } from "../SignRequests.styles";
import signRequestsTokens, {
  signRequestsStatusTokens,
} from "../SignRequests.tokens";
import filtersStyles from "../SignRequestsFilters.styles";
import signRequestsFiltersTokens from "../SignRequestsFilters.tokens";
import { getRequestExpiry, getRequestTags } from "../SignRequestsFiltersHelper";
import {
  getSignerInitials,
  getSignerTooltip,
  getSignRequestsRowStatus,
} from "../SignRequestsRowHelper";
import { DocumentGlyph, KebabGlyph, TagGlyph } from "./SignRequestsIcons";

const styles = signRequestsStyles;
const cells = filtersStyles;
const { avatars } = signRequestsTokens;
const { table, tags: tagTokens } = signRequestsFiltersTokens;

interface SentRequestRowProps {
  request: SignRequest;
  isLoading: boolean;
  onOpen: (request: SignRequest) => void;
  onMenu: (request: SignRequest, anchor: HTMLElement) => void;
  /**
   * Draw the Tags column and the tag trigger. Off for a list read from the
   * folder endpoint, whose rows carry no tags.
   */
  withTags: boolean;
  /** This row's tag picker is the one currently open. */
  isTagPickerOpen?: boolean;
  onTag?: (request: SignRequest, anchor: HTMLElement) => void;
}

/**
 * One request row of the Sign Requests table: subject, signer avatars, sender,
 * status, tags, created and expiry dates, then the tag trigger and the
 * overflow menu.
 *
 * The cells are flat children of the row grid, in column order, and each one
 * hides itself at the widths its column does not exist at — so the same markup
 * serves the eight-column desktop table, the narrower tablet ones, and the
 * phone, where the reference turns the row into a three-line card (subject +
 * kebab, status chip + avatars, progress bar) placed by named grid area.
 */
const SentRequestRow = ({
  request,
  isLoading,
  onOpen,
  onMenu,
  withTags,
  isTagPickerOpen = false,
  onTag,
}: SentRequestRowProps) => {
  const status = getSignRequestsRowStatus(request);
  const tone = signRequestsStatusTokens[status.key];

  /** CC recipients aren't signers, so they stay out of the avatar stack. */
  const signers = (request.signers ?? []).filter(
    (signer: RequestParticipant) => !signer.cc,
  );
  const visible = signers.slice(0, avatars.maxVisible);
  const overflow = signers.length - visible.length;

  const formatCreatedOn = (format: string) =>
    request.createdOn
      ? convertESTtoUserLocalDateAndTime(
          request.createdOn,
          format,
          "MM/DD/YYYY HH:mm:ss",
        )
      : "";
  const createdOn = formatCreatedOn("MMM D, YYYY, h:mm A");
  const createdDate = formatCreatedOn("MMM D, YYYY");
  const expiry = getRequestExpiry(request);

  const rowTags = withTags ? getRequestTags(request) : [];
  const visibleTags = rowTags.slice(0, tagTokens.maxVisible);
  const hiddenTags = rowTags.slice(tagTokens.maxVisible);

  return (
    <Box
      sx={styles.requestRow(withTags)}
      onClick={() => {
        if (!isLoading) onOpen(request);
      }}
    >
      <Box sx={styles.rowLead}>
        <Box sx={styles.iconTile(tone.iconBg)}>
          <DocumentGlyph sx={styles.requestGlyph(tone.iconStroke)} />
        </Box>
        <Box sx={styles.rowText}>
          <Tooltip title={request.emailSubject} arrow>
            <Typography
              component="div"
              sx={{
                ...styles.rowTitle(status.muted),
                "&:focus-visible": {
                  outline: `2px solid ${focusRing}`,
                  outlineOffset: "2px",
                },
              }}
              role="button"
              tabIndex={0}
              aria-label={`Open request ${request.emailSubject}`}
              onClick={() => {
                if (!isLoading) onOpen(request);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  if (!isLoading) onOpen(request);
                }
              }}
            >
              {request.emailSubject}
            </Typography>
          </Tooltip>
          <Typography component="div" sx={styles.requestSub}>
            Created by {request.requestedBy?.name}
            {createdOn ? ` · ${createdOn}` : ""}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.signersColumn}>
        {signers.length > 0 ? (
          <Box sx={styles.avatarStack}>
            {visible.map((signer: RequestParticipant, index: number) => {
              /*
                An in-person signer has no name until the host captures it at
                handover, so both the initials and the tooltip fall back to
                whatever the request does know about them.
              */
              /*
                Initials come from a real identity only — a signer nobody has
                named yet keeps MUI's person glyph, which is honest, where
                initials off "In-person signer" would read as a person called
                "IS".
              */
              const identity =
                (signer.name ?? "").trim() || (signer.email ?? "").trim();
              return (
                <Tooltip
                  key={signer.id ?? `${signer.email}-${index}`}
                  title={
                    <Box sx={styles.avatarTooltip}>
                      {getSignerTooltip(signer)}
                    </Box>
                  }
                  arrow
                >
                  <Avatar
                    sx={styles.avatar(
                      getAvatarColors(identity[0] ?? "A").bg,
                      index === 0,
                    )}
                  >
                    {getSignerInitials(identity)}
                  </Avatar>
                </Tooltip>
              );
            })}
            {overflow > 0 && (
              <Avatar sx={styles.avatarMore}>{`+${overflow}`}</Avatar>
            )}
          </Box>
        ) : (
          <Typography component="span" sx={styles.avatarsEmpty}>
            {avatars.emptyLabel}
          </Typography>
        )}
      </Box>

      <Box sx={cells.senderCell}>{request.requestedBy?.name}</Box>

      <Box sx={styles.statusColumn}>
        <Box sx={styles.statusChip(status.key)}>
          <Box component="span" sx={styles.statusDot(status.key)} />
          <Box component="span" sx={styles.statusLabel(status.key)}>
            {status.label}
          </Box>
        </Box>
        <Box sx={styles.progressRow}>
          <Box sx={styles.progressTrack}>
            <Box sx={styles.progressFill(status.key, status.percent)} />
          </Box>
          <Typography component="span" sx={styles.progressNote}>
            {status.note}
          </Typography>
        </Box>
      </Box>

      {withTags && (
        <Box sx={cells.tagsCell}>
          {visibleTags.map((tag) => (
            <Box
              key={tag.id}
              component="span"
              title={tag.name}
              sx={cells.tagChip(tag.color, rowTags.length === 1)}
            >
              {tag.name}
            </Box>
          ))}
          {hiddenTags.length > 0 && (
            <Box
              component="span"
              title={hiddenTags.map((tag) => tag.name).join(", ")}
              sx={cells.tagMore}
            >
              +{hiddenTags.length}
            </Box>
          )}
          {rowTags.length === 0 && (
            <Box component="span" aria-label="No tags" sx={cells.cellEmpty}>
              {table.emptyValue}
            </Box>
          )}
        </Box>
      )}

      <Box sx={cells.createdCell}>{createdDate || table.emptyValue}</Box>

      <Box sx={cells.expiresCell}>
        <Box component="span" sx={cells.expiresDate}>
          {expiry.label}
        </Box>
        {expiry.soon && (
          <Box component="span" sx={cells.expiresSoon}>
            {expiry.soon}
          </Box>
        )}
      </Box>

      <Box sx={styles.actionsColumn}>
        {withTags && onTag && (
          <Box
            component="span"
            className={TAG_ACTION_CLASS}
            sx={styles.tagAction(isTagPickerOpen)}
          >
            <IconButton
              sx={cells.tagTrigger(isTagPickerOpen)}
              disabled={isLoading}
              aria-label={`Tag "${request.emailSubject}"`}
              aria-haspopup="dialog"
              onClick={(event) => {
                event.stopPropagation();
                onTag(request, event.currentTarget);
              }}
            >
              <TagGlyph sx={cells.tagTriggerGlyph} />
            </IconButton>
          </Box>
        )}
        <IconButton
          sx={styles.kebabButton}
          disabled={isLoading}
          aria-label={`Options for request ${request.emailSubject}`}
          onClick={(event) => {
            event.stopPropagation();
            onMenu(request, event.currentTarget);
          }}
        >
          <KebabGlyph sx={styles.kebabGlyph} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SentRequestRow;
